import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, FormControlLabel, IconButton, MenuItem, Paper, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
    Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import { CommercialPartyService } from '../../services/commercialParty.service';
import {
    COMMERCIAL_PARTY_TYPE_LABELS, COMMERCIAL_PARTY_TYPES, CommercialPartyType, ICommercialParty,
} from '../../interfaces/commercialParty.interface';
import CommercialPartyFormDialog from './CommercialPartyFormDialog';
import { humanizeEnum } from '../../utils/format';

/** Falls back to the raw enum value for any type without a friendlier
 * override in `COMMERCIAL_PARTY_TYPE_LABELS`. */
const partyTypeLabel = (type: CommercialPartyType): string => COMMERCIAL_PARTY_TYPE_LABELS[type] ?? humanizeEnum(type);

/** Commercial party master (Task 4a, spec §2, 7.4). Guarded on LC_VIEW/LC_MANAGE, matching
 * CommercialPartyController's `@authz.can` exactly -- reused keys, no dedicated permission
 * catalog entry. No delete route -- referenced master data gets Activate/Deactivate instead. */
const CommercialPartyList: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.LC_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [rows, setRows] = useState<ICommercialParty[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [type, setType] = useState<'ALL' | CommercialPartyType>('ALL');
    const [activeOnly, setActiveOnly] = useState(false);
    const [q, setQ] = useState('');
    // Debounced 300ms behind `q` so a reload isn't fired on every keystroke -- LIKE-wildcard
    // handling stays entirely on the backend, this only delays when the request goes out.
    const [debouncedQ, setDebouncedQ] = useState('');
    const [dialogTarget, setDialogTarget] = useState<ICommercialParty | 'new' | null>(null);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedQ(q), 300);
        return () => clearTimeout(handle);
    }, [q]);

    const load = async () => {
        try {
            setLoading(true);
            const res = await CommercialPartyService.list(institutionId, {
                page, size,
                type: type === 'ALL' ? undefined : type,
                activeOnly: activeOnly || undefined,
                q: debouncedQ.trim() || undefined,
            });
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load commercial parties');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size, type, activeOnly, debouncedQ]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggleActive = async (row: ICommercialParty) => {
        try {
            if (row.active) {
                await CommercialPartyService.deactivate(institutionId, row.id);
                toast.showSuccess('Party deactivated');
            } else {
                await CommercialPartyService.activate(institutionId, row.id);
                toast.showSuccess('Party activated');
            }
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to update party status');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.parties.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Commercial Parties</Typography>
                {canManage && (
                    <Button data-help="commercial.parties.new" variant="contained" startIcon={<AddIcon />} onClick={() => setDialogTarget('new')}>
                        New Party
                    </Button>
                )}
            </Box>

            <Box data-help-section="commercial.parties.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                    data-help="commercial.parties.typeFilter" select label="Type" size="small" value={type}
                    onChange={(e) => { setType(e.target.value as 'ALL' | CommercialPartyType); setPage(0); }}
                    sx={{ flex: '1 1 220px', maxWidth: 260 }}
                >
                    <MenuItem value="ALL">All</MenuItem>
                    {COMMERCIAL_PARTY_TYPES.map(t => <MenuItem key={t} value={t}>{partyTypeLabel(t)}</MenuItem>)}
                </TextField>
                <TextField
                    data-help="commercial.parties.search" label="Search (code / name)" size="small" value={q}
                    onChange={(e) => { setQ(e.target.value); setPage(0); }}
                    sx={{ flex: '1 1 220px', maxWidth: 320 }}
                />
                <FormControlLabel
                    data-help="commercial.parties.activeOnly"
                    sx={{ flex: '1 1 220px', maxWidth: 220 }}
                    control={<Switch checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); setPage(0); }} />}
                    label="Active only"
                />
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.parties.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.parties.col.code">Code</TableCell>
                                <TableCell data-help="commercial.parties.col.name">Name</TableCell>
                                <TableCell data-help="commercial.parties.col.type">Type</TableCell>
                                <TableCell data-help="commercial.parties.col.country">Country</TableCell>
                                <TableCell data-help="commercial.parties.col.license">License No</TableCell>
                                <TableCell data-help="commercial.parties.col.status">Status</TableCell>
                                {canManage && <TableCell align="right" data-help="commercial.parties.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow key={r.id} hover data-testid={`commercial-party-row-${r.id}`}>
                                    <TableCell>{r.code}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>{partyTypeLabel(r.partyType)}</TableCell>
                                    <TableCell>{r.countryCode ?? '—'}</TableCell>
                                    <TableCell>{r.licenseNo ?? '—'}</TableCell>
                                    <TableCell>
                                        <Chip data-help="commercial.parties.status" size="small" label={r.active ? 'Active' : 'Inactive'} color={r.active ? 'success' : 'default'} />
                                    </TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <IconButton size="small" aria-label="edit" data-help="commercial.parties.edit" onClick={() => setDialogTarget(r)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <Tooltip title={r.active ? 'Deactivate' : 'Activate'}>
                                                <IconButton
                                                    size="small"
                                                    aria-label={r.active ? 'deactivate' : 'activate'}
                                                    data-help="commercial.parties.toggleActive"
                                                    onClick={() => handleToggleActive(r)}
                                                >
                                                    {r.active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={canManage ? 7 : 6} align="center">No commercial parties found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}

            {dialogTarget && (
                <CommercialPartyFormDialog
                    institutionId={institutionId}
                    target={dialogTarget === 'new' ? null : dialogTarget}
                    onClose={() => setDialogTarget(null)}
                    onSaved={() => { setDialogTarget(null); load(); }}
                />
            )}
        </Box>
    );
};

export default CommercialPartyList;
